/**
 * Browser-Error-Reporter (Saso 2026-08-20): jede unbehandelte Exception
 * und jede abgewiesene Promise geht an POST /api/errors — mit Offline-
 * Queue (localStorage), damit Fehler nach Verbindungsabbruch nicht verloren
 * gehen. Der Endpoint ist rate-limited; hier drosseln wir zusaetzlich auf
 * Client-Seite (max 1 Report pro identischer Message pro Minute).
 *
 * Kein PII: Message, Stack, URL, Session-ID (zufaellig), User-Agent.
 * Der Server sanitized den Context nochmal (defense in depth).
 */

const QUEUE_KEY = 'consultants:error-queue';
const MAX_QUEUE = 20;
const SESSION_ID = (() => {
  try {
    const existing = localStorage.getItem('consultants:error-session');
    if (existing) return existing;
    const id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem('consultants:error-session', id);
    return id;
  } catch {
    return 'no-session';
  }
})();

interface QueuedReport {
  message: string;
  level: 'error' | 'warn' | 'fatal';
  code?: string;
  stack?: string | null;
  url?: string | null;
  sessionId?: string | null;
  context?: Record<string, unknown>;
  ts: number;
}

const recentReports = new Map<string, number>();

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)cst_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : '';
}

function enqueue(report: QueuedReport): void {
  try {
    const queue: QueuedReport[] = JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]');
    queue.push(report);
    // Aelteste rauswerfen, Queue begrenzen (kein localStorage-Flood).
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch { /* localStorage voll/verboten — Fehler ist dann nur in der Konsole */ }
}

async function flush(): Promise<void> {
  let queue: QueuedReport[] = [];
  try {
    queue = JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]');
  } catch { return; }
  if (queue.length === 0) return;
  // Bis zu 5 pro Flush — der Rest wartet auf den naechsten.
  const batch = queue.slice(0, 5);
  const rest = queue.slice(5);
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(rest));
  } catch { /* ignore */ }
  for (const report of batch) {
    try {
      await fetch('/api/errors', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken() },
        credentials: 'same-origin',
        body: JSON.stringify({
          message: report.message,
          level: report.level,
          code: report.code,
          stack: report.stack,
          url: report.url,
          sessionId: SESSION_ID,
          context: report.context,
        }),
      });
    } catch {
      // Offline/Server down: Report zurueck in die Queue.
      enqueue(report);
      return;
    }
  }
}

function report(
  message: string,
  extras: { level?: QueuedReport['level']; code?: string; stack?: string | null; url?: string | null; context?: Record<string, unknown> },
  dedupeMs = 60_000,
): void {
  const now = Date.now();
  const last = recentReports.get(message) ?? 0;
  if (now - last < dedupeMs) return; // identische Meldung gerade erst gemeldet
  recentReports.set(message, now);
  enqueue({ message, level: extras.level ?? 'error', code: extras.code, stack: extras.stack ?? null, url: extras.url ?? location.href, ts: now, context: extras.context });
  void flush();
}

/** Installiert die globalen Handler. Einmal beim App-Start aufrufen. */
export function installErrorReporter(): void {
  window.addEventListener('error', (event) => {
    report(
      event.message || 'Unbekannter Script-Fehler',
      {
        code: 'window_error',
        stack: event.error instanceof Error ? (event.error.stack ?? null) : null,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          appVersion: (document.documentElement.dataset.appVersion as string | undefined) ?? null,
        },
      },
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    report(
      reason instanceof Error ? reason.message : String(reason ?? 'Unhandled Promise Rejection'),
      {
        code: 'unhandled_rejection',
        stack: reason instanceof Error ? (reason.stack ?? null) : null,
      },
    );
  });

  // Beim Online-Werden: Queue nachliefern.
  window.addEventListener('online', () => void flush());
  // Und einmal beim Start (falls von der letzten Session was liegen blieb).
  void flush();
}
